import React, { useEffect, useRef } from "react";
import { isSet, evaluate } from "../../../../../../utils/commonUtils";
import FormInputs from "../../../../../../components/UI/FormInputs";
import RadioGroup from "../../../../../../components/UI/FormInputs/RadioGroup";

const CertificationForm = ({
    fields = [],
    formData = {},
    changeHandler,
    updateFormData,
    readOnly,
    validation = {},
    item,
    ...props
}) => {
    // Extract fields from item if provided, otherwise use fields prop
    const formFields = item && item.fields ? item.fields : fields;
    
    // Ensure formData is always an object
    const safeFormData = formData || {};
    const safeValidation = validation || {};
    
    const handleFieldChange = (name, value) => {
        if (changeHandler) {
            changeHandler(name, value);
        }
    };

    const renderField = (field, index) => {
        if (!field) return null;

        // Evaluate rules for this field
        let result = null;
        let action = '';
        let attr = {};
        let shouldClear = false;
        let shouldEnable = false;
        let shouldRequired = false;
        
        if (isSet(field.rules, []).length > 0 && safeFormData) {
            try {
                const rules = isSet(field.rules, []);
                
                // First, check for SHOW/HIDE rules separately to handle visibility
                let showResult = null;
                let hideResult = null;
                rules.forEach((ruleItem) => {
                    if (ruleItem.action === 'SHOW' || ruleItem.action === 'HIDE') {
                        const resultData = evaluate([ruleItem], safeFormData);
                        if (resultData) {
                            if (resultData.action === 'SHOW') {
                                showResult = resultData.result;
                            }
                            if (resultData.action === 'HIDE') {
                                hideResult = resultData.result;
                            }
                        }
                    }
                });
                
                // Handle field visibility early - SHOW takes precedence over HIDE
                if (showResult !== null) {
                    if (showResult === false) return null; // Hide if SHOW is false
                    action = 'SHOW';
                    result = showResult;
                } else if (hideResult !== null && hideResult === true) {
                    return null; // Hide if HIDE is true
                }
                
                // Process all rules to check for CLEAR, ENABLE, and REQUIRED actions
                rules.forEach((ruleItem) => {
                    const resultData = evaluate([ruleItem], safeFormData);
                    if (resultData) {
                        if (resultData.action === 'CLEAR' && resultData.result === true) {
                            shouldClear = true;
                        }
                        if (resultData.action === 'ENABLE' && resultData.result === true) {
                            shouldEnable = true;
                        }
                        if (resultData.action === 'REQUIRED' && resultData.result === true) {
                            shouldRequired = true;
                        }
                    }
                });
                
                // Get the last rule result for other actions (DISABLE, etc.)
                // Only if we haven't already set action from SHOW
                if (action !== 'SHOW') {
                    const resultData = evaluate(rules, safeFormData);
                    if (resultData) {
                        action = resultData.action;
                        result = resultData.result;
                    }
                }
            } catch (error) {
                console.error('Error evaluating rules:', error);
            }
        }

        // Handle field enable/disable - ENABLE takes precedence
        if (shouldEnable || (action === 'ENABLE' && result === true)) {
            attr = { ...attr, disabled: false };
        } else if (action === 'DISABLE' && result === true) {
            attr = { ...attr, disabled: true };
        }
        
        // Handle REQUIRED action - can be combined with ENABLE
        if (shouldRequired || (action === 'REQUIRED' && result === true)) {
            attr = { ...attr, required: true, disabled: false };
        }
        
        if (shouldClear && !shouldEnable && !(action === 'REQUIRED' && result === true)) {
            // Handle CLEAR action (only if ENABLE is definitely not true)
            // Only clear if the field has a value and should be cleared
            const key = field.name || field.id;
            // For checkboxes, check if value is truthy (1, true). For other fields, check if not empty/null/undefined
            const hasValue = field.type === 'Checkbox' 
                ? (safeFormData[key] === 1 || safeFormData[key] === true)
                : (safeFormData[key] !== "" && safeFormData[key] !== null && safeFormData[key] !== undefined);
            if (key && hasValue && updateFormData) {
                // Re-evaluate rules to ensure we should still clear (prevent clearing on initial load with valid data)
                const rules = isSet(field.rules, []);
                let finalShouldClear = false;
                let finalShouldEnable = false;
                
                rules.forEach((ruleItem) => {
                    const resultData = evaluate([ruleItem], safeFormData);
                    if (resultData) {
                        if (resultData.action === 'CLEAR' && resultData.result === true) {
                            finalShouldClear = true;
                        }
                        if (resultData.action === 'ENABLE' && resultData.result === true) {
                            finalShouldEnable = true;
                        }
                    }
                });
                
                // Only clear if CLEAR is true AND ENABLE is false
                if (finalShouldClear && !finalShouldEnable) {
                    setTimeout(() => {
                        // For checkboxes, clear to 0 instead of empty string
                        const clearValue = field.type === 'Checkbox' ? 0 : "";
                        updateFormData({ [key]: clearValue });
                    }, 0);
                }
            }
            attr = { ...attr, disabled: true };
        }

        // Apply disabled state from field config
        if (field.disabled === true && attr.disabled !== false) {
            attr = { ...attr, disabled: true };
        }

        const value = field.name ? isSet(safeFormData[field.name], '') : '';
        const column = isSet(field.col, 'col-12');

        // Render based on field type
        switch (field.type) {
            case 'RadioGroup':
                // Remove bold styling from options by adding custom labelStyle that overrides bold
                const optionsWithoutBold = (field.options || []).map(opt => ({
                    ...opt,
                    labelStyle: { fontWeight: 'normal', ...(opt.labelStyle || {}) }
                }));
                
                const radioName = field.name;
                const radioId = field.id || field.name;
                const radioRef = React.createRef();
                
                // Component to fix label htmlFor attributes
                const RadioGroupWithFixedLabels = () => {
                    const containerRef = useRef(null);
                    
                    useEffect(() => {
                        // Fix label htmlFor to match input id
                        if (containerRef.current) {
                            const inputs = containerRef.current.querySelectorAll('input[type="radio"]');
                            inputs.forEach((input, idx) => {
                                const correctId = `${radioId}_${idx}`;
                                if (input.id !== correctId) {
                                    input.id = correctId;
                                }
                                // Find the label and fix its htmlFor
                                const label = containerRef.current.querySelector(`label[for="${radioName}_${idx}"]`);
                                if (label) {
                                    label.setAttribute('for', correctId);
                                }
                            });
                        }
                    }, [value]);
                    
                    return (
                        <div ref={containerRef}>
                            <RadioGroup
                                name={radioName}
                                id={radioId}
                                value={value}
                                options={optionsWithoutBold}
                                changeHandler={(val) => handleFieldChange(field.name, val)}
                                readOnly={readOnly || field.readOnly}
                                disabled={attr.disabled}
                                className="form-check-input"
                                labelStyle={{ fontWeight: 'normal' }}
                            />
                        </div>
                    );
                };
                
                return (
                    <div className={`${column}`} key={index} style={{ marginBottom: '4px' }}>
                        <div className="form-group mb-1">
                            <RadioGroupWithFixedLabels />
                            <style>{`
                                label[for^="${radioId}_"] {
                                    font-weight: normal !important;
                                    cursor: ${(readOnly || attr.disabled) ? 'not-allowed' : 'pointer'} !important;
                                }
                                label[for^="${radioId}_"]:hover {
                                    opacity: ${(readOnly || attr.disabled) ? '0.6' : '0.8'};
                                }
                            `}</style>
                        </div>
                    </div>
                );

            case 'Checkbox':
                const checkboxId = field.id || field.name;
                return (
                    <div className={`${column}`} key={index} style={{ marginBottom: '4px' }}>
                        <div className="form-group mb-1">
                            <div className="form-check d-inline-flex align-items-center">
                                <FormInputs
                                    type="Checkbox"
                                    name={field.name}
                                    id={checkboxId}
                                    value={safeFormData[field.name] || false}
                                    changeHandler={(val) => handleFieldChange(field.name, val)}
                                    readOnly={readOnly || field.readOnly}
                                    disabled={attr.disabled}
                                    className="form-check-input"
                                />
                                {field.label && (
                                    <label 
                                        className="form-check-label pl-2 mb-0" 
                                        htmlFor={checkboxId}
                                        style={{ cursor: (readOnly || attr.disabled) ? 'not-allowed' : 'pointer', fontWeight: 'normal' }}
                                    >
                                        {field.label}
                                    </label>
                                )}
                            </div>
                        </div>
                    </div>
                );

            case 'TextInput':
                // Merge required prop - attr.required takes precedence over field.required
                const textInputRequired = attr.required !== undefined ? attr.required : (field.required || false);
                const textInputMaxLen = field.max !== undefined && field.max !== null ? field.max : field.Max;
                return (
                    <div className={`${column}`} key={index} style={{ marginBottom: '4px', marginLeft: field.inline ? '10px' : '0' }}>
                        <div className="form-group mb-1">
                            <FormInputs
                                type="TextInput"
                                name={field.name}
                                id={field.id || field.name}
                                value={value}
                                placeholder={field.placeholder || ''}
                                changeHandler={(val) => handleFieldChange(field.name, val)}
                                readOnly={readOnly || field.readOnly}
                                disabled={attr.disabled}
                                required={textInputRequired}
                                max={textInputMaxLen}
                                className="form-control"
                                style={textInputRequired ? { borderLeft: '2px solid red', ...(field.style || {}) } : (field.style || {})}
                            />
                            {field.name && isSet(safeValidation[field.name], "") !== "" && (
                                <span className="text-danger m-1">
                                    {isSet(safeValidation[field.name], "")}
                                </span>
                            )}
                        </div>
                    </div>
                );

            case 'Label':
                return (
                    <div className={`${column} border border-left-0 py-1`} key={index}>
                        <div className="form-group row mb-0">
                            <label className="col-sm-12 f-400 text-sm mb-0">
                                {field.label}
                            </label>
                        </div>
                    </div>
                );

            case 'NewSignature':
                // Simplified wrapper for NewSignature (handles visibility internally)
                const signatureRequired = attr.required !== undefined ? attr.required : (field.required || false);
                return (
                    <div className={`${column} border border-left-0 py-1`} key={index}>
                        <div className="form-group mb-0">
                            {field.isLabel !== false && field.label && (
                                <label className="f-400 text-sm mb-2 d-block opacity-75" title={field.label}>
                                    {field.label}
                                </label>
                            )}
                            <div style={{ width: '100%' }}>
                                <FormInputs
                                    {...field}
                                    {...attr}
                                    required={signatureRequired}
                                    value={value}
                                    changeHandler={(val) => handleFieldChange(field.name, val)}
                                    readOnly={readOnly || field.readOnly}
                                />
                                {field.name && isSet(safeValidation[field.name], "") !== "" && (
                                    <span className="text-danger m-1">
                                        {isSet(safeValidation[field.name], "")}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                );

            default:
                // Merge required prop - attr.required takes precedence over field.required
                const mergedRequired = attr.required !== undefined ? attr.required : (field.required || false);
                const mergedStyle = mergedRequired ? { borderLeft: '2px solid red', ...(field.style || {}) } : (field.style || {});
                return (
                    <div className={`${column} border border-left-0 py-1`} key={index}>
                        <div className="form-group row mb-0 align-items-center">
                            {field.isLabel !== false && field.label && (
                                <label className="col-sm-6 f-400 text-sm mb-0 opacity-75" title={field.label}>
                                    {field.label}
                                </label>
                            )}
                            <div className={field.isLabel === false ? "col-sm-12" : "col-sm-6"}>
                                <FormInputs
                                    {...field}
                                    {...attr}
                                    required={mergedRequired}
                                    style={mergedStyle}
                                    value={value}
                                    changeHandler={(val) => handleFieldChange(field.name, val)}
                                    readOnly={readOnly || field.readOnly}
                                />
                                {field.name && isSet(safeValidation[field.name], "") !== "" && (
                                    <span className="text-danger m-1">
                                        {isSet(safeValidation[field.name], "")}
                                    </span>
                                )}
                            </div>
                        </div>
                    </div>
                );
        }
    };

    // Check if we need to handle "Other (specify):" inline
    const processedFields = [];
    for (let i = 0; i < formFields.length; i++) {
        const field = formFields[i];
        const nextField = formFields[i + 1];
        
        // Check if this is "Monitoring Duration" RadioGroup with "Other (specify):" option
        if (field.type === 'RadioGroup' && 
            field.name === 'monitoring_duration' &&
            field.options && 
            field.options.some(opt => opt.label && opt.label.includes('Other (specify)')) &&
            nextField && 
            nextField.type === 'TextInput' &&
            nextField.name === 'monitoring_duration_other') {
            // Mark this field to render inline with next field
            processedFields.push({ ...field, renderInline: true, inlineTextInput: nextField });
            i++; // Skip next field as it will be rendered inline
            continue;
        }
        processedFields.push(field);
    }

    return (
        <div className="row" style={{ margin: 0 }}>
            {processedFields.map((field, index) => {
                // Handle inline rendering for "Other (specify):" case
                if (field.renderInline && field.inlineTextInput) {
                    const textInputField = field.inlineTextInput;
                    let textInputAttr = {};
                    if (isSet(textInputField.rules, []).length > 0 && safeFormData) {
                        try {
                            const resultData = evaluate(isSet(textInputField.rules, []), safeFormData);
                            if (resultData.action === 'ENABLE' && resultData.result === true) {
                                textInputAttr = { disabled: false };
                            } else if (resultData.action === 'REQUIRED' && resultData.result === true) {
                                textInputAttr = { disabled: false, required: true };
                            }
                        } catch (error) {
                            console.error('Error evaluating rules:', error);
                        }
                    }
                    if (textInputField.disabled === true && textInputAttr.disabled !== false) {
                        textInputAttr = { ...textInputAttr, disabled: true };
                    }
                    
                    // Remove bold styling from options
                    const optionsWithoutBold = (field.options || []).map(opt => ({
                        ...opt,
                        labelStyle: { fontWeight: 'normal', ...(opt.labelStyle || {}) }
                    }));
                    
                    const radioId = field.id || field.name;
                    
                    return (
                        <div key={index} className="col-12" style={{ marginBottom: '4px' }}>
                            <div className="form-group mb-1">
                                <div className="d-flex align-items-center flex-wrap">
                                    <RadioGroup
                                        name={field.name}
                                        id={radioId}
                                        value={isSet(safeFormData[field.name], '')}
                                        options={optionsWithoutBold}
                                        changeHandler={(val) => handleFieldChange(field.name, val)}
                                        readOnly={readOnly || field.readOnly}
                                        disabled={field.disabled}
                                        className="form-check-input"
                                        labelStyle={{ fontWeight: 'normal' }}
                                    />
                                    <div style={{ marginLeft: '10px', flex: '1', minWidth: '200px', maxWidth: '300px' }}>
                                        {(() => {
                                            // Merge required prop - textInputAttr.required takes precedence over textInputField.required
                                            const inlineRequired = textInputAttr.required !== undefined ? textInputAttr.required : (textInputField.required || false);
                                            return (
                                                <FormInputs
                                                    type="TextInput"
                                                    name={textInputField.name}
                                                    id={textInputField.id || textInputField.name}
                                                    value={isSet(safeFormData[textInputField.name], '')}
                                                    placeholder={textInputField.placeholder || ''}
                                                    changeHandler={(val) => handleFieldChange(textInputField.name, val)}
                                                    readOnly={readOnly || textInputField.readOnly}
                                                    disabled={textInputAttr.disabled}
                                                    required={inlineRequired}
                                                    className="form-control"
                                                    style={inlineRequired ? { borderLeft: '2px solid red', ...(textInputField.style || {}) } : (textInputField.style || {})}
                                                />
                                            );
                                        })()}
                                    </div>
                                </div>
                                <style>{`
                                    label[for^="${radioId}_"] {
                                        font-weight: normal !important;
                                    }
                                `}</style>
                            </div>
                        </div>
                    );
                }
                return renderField(field, index);
            })}
        </div>
    );
};

export default CertificationForm;

